import React, { useCallback, useMemo, useState } from 'react';

import { assert } from '@gitbook/util-ts';

import type { AnimatedState } from '../animations';

type DialogStackContextType = {
    stack: string[];
    add: (id: string) => void;
    remove: (id: string) => void;
};

export const DialogStackContext = React.createContext<DialogStackContextType | null>(null);

export function DialogStackProvider({ children }: { children: React.ReactNode }) {
    const parentContext = React.useContext(DialogStackContext);

    if (parentContext) {
        return <>{children}</>;
    }

    return <DialogStackInitializer>{children}</DialogStackInitializer>;
}

function DialogStackInitializer({ children }: { children: React.ReactNode }) {
    // Create local state if no parent exists
    const [stack, setLocalDialogStack] = useState<string[]>([]);

    const add = useCallback((id: string) => {
        setLocalDialogStack((prev) => {
            if (!prev.includes(id)) {
                return [...prev, id];
            }
            return prev;
        });
    }, []);

    const remove = useCallback((id: string) => {
        setLocalDialogStack((prev) => prev.filter((dialogId) => dialogId !== id));
    }, []);

    const value = useMemo(() => ({ stack, add, remove }), [stack, add, remove]);

    return <DialogStackContext.Provider value={value}>{children}</DialogStackContext.Provider>;
}

/**
 * Hook to manage the dialog stack.
 */
export function useStackDialog(input: { id: string; animatedState: AnimatedState }) {
    const { id, animatedState } = input;
    const ctx = React.useContext(DialogStackContext);
    assert(ctx !== null, 'Dialog must be used within a DialogStackProvider');
    const { add, remove, stack } = ctx;

    const isRoot = stack[0] === id;
    const position = React.useMemo(
        () => Math.max(0, [...stack].reverse().indexOf(id)),
        [stack, id]
    );

    /* Handle stack updates based on state */
    React.useLayoutEffect(() => {
        if (animatedState === 'entering' || animatedState === 'entered') {
            add(id);
        } else if (animatedState === 'exiting') {
            remove(id);
        }
    }, [animatedState, add, remove, id]);

    /* Handle cleanup on actual unmount */
    React.useLayoutEffect(() => {
        return () => {
            remove(id);
        };
    }, [remove, id]);

    return [position, isRoot] as const;
}
